import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteCatalogEntry, useSaveCatalogEntry } from '../../mutations';
import { useCatalog } from '../../queries';
import type { CatalogEntry, CatalogKind } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Input } from '@/shared/ui/atoms/Input';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import '../RolesManager/RolesManager.styl';

const valid = (name: string) => name.trim().length >= 2 && name.trim().length <= 80;

/** Maintains one member catalogue (member types or contract types): add, rename, delete when unused. */
export function CatalogManager({ kind }: { kind: CatalogKind }) {
  const { t } = useTranslation(undefined, { keyPrefix: `catalogs.${kind}` });
  const { t: tc } = useTranslation(undefined, { keyPrefix: 'catalogs' });
  const entries = useCatalog(kind);
  const save = useSaveCatalogEntry(kind);
  const toast = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (entries.isPending) return <Spinner label={tc('loading')} />;

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!valid(name)) return setError(tc('nameInvalid'));
    setError(null);
    save.mutate(
      { name: name.trim() },
      {
        onSuccess: () => {
          setName('');
          toast.show(tc('created'), 'success');
        },
        onError: (err) => setError(errorMessage(err, tc('saveError')))
      }
    );
  };

  return (
    <div className="f-roles">
      <p className="f-roles__hint">{t('intro')}</p>
      <form className="f-roles__bar" onSubmit={add} noValidate>
        <Input icon="pencil" aria-label={t('name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('placeholder')} />
        <Button variant="add" type="submit" size="sm" iconLeft={<FontIcon name="plus-circle" />} loading={save.isPending}>
          {t('add')}
        </Button>
      </form>
      {error ? (
        <div className="f-newtask__error" role="alert">
          {error}
        </div>
      ) : null}
      {(entries.data ?? []).length === 0 ? (
        <p className="f-roles__hint">{tc('empty')}</p>
      ) : (
        <ul className="f-roles__list">
          {(entries.data ?? []).map((entry) => (
            <EntryRow key={entry.id} kind={kind} entry={entry} />
          ))}
        </ul>
      )}
    </div>
  );
}

function EntryRow({ kind, entry }: { kind: CatalogKind; entry: CatalogEntry }) {
  const { t } = useTranslation(undefined, { keyPrefix: `catalogs.${kind}` });
  const { t: tc } = useTranslation(undefined, { keyPrefix: 'catalogs' });
  const save = useSaveCatalogEntry(kind);
  const remove = useDeleteCatalogEntry(kind);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(entry.name);

  const rename = (e: FormEvent) => {
    e.preventDefault();
    if (!valid(name)) return toast.show(tc('nameInvalid'), 'error');
    save.mutate(
      { id: entry.id, name: name.trim() },
      {
        onSuccess: () => {
          setEditing(false);
          toast.show(tc('renamed'), 'success');
        },
        onError: (err) => toast.show(errorMessage(err, tc('saveError')), 'error')
      }
    );
  };

  if (editing) {
    return (
      <li className="f-roles__item">
        <form className="f-roles__bar" onSubmit={rename} noValidate>
          <Input icon="pencil" aria-label={t('name')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <Button type="submit" size="sm" loading={save.isPending}>
            {tc('save')}
          </Button>
          <Button size="sm" variant="cancel" onClick={() => { setName(entry.name); setEditing(false); }}>
            {tc('cancel')}
          </Button>
        </form>
      </li>
    );
  }

  return (
    <li className="f-roles__item">
      <div className="f-roles__main">
        <div className="f-roles__name">{entry.name}</div>
      </div>
      <div className="f-roles__actions">
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          {tc('rename')}
        </Button>
        {confirming ? (
          <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(entry.id, { onSuccess: () => toast.show(tc('deleted'), 'success'), onError: (err) => toast.show(errorMessage(err, tc('deleteError')), 'error'), onSettled: () => setConfirming(false) })}>
            {tc('confirmDelete')}
          </Button>
        ) : (
          <Button size="sm" variant="ghost" iconLeft={<FontIcon name="bin" />} onClick={() => setConfirming(true)}>
            {tc('delete')}
          </Button>
        )}
      </div>
    </li>
  );
}
